import React from 'react';
import { Pressable } from 'react-native';
import { XStack, SizableText } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { AntDesign } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import AppSelect from '@components/Global/select';

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  nextPage?: number;
  prevPage?: number;
}

export interface PaginationProps {
  pagination: PaginationInfo;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  showTotalRecords?: boolean;
  compact?: boolean;
  style?: any;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortByChange?: (sortBy: string) => void;
  onSortOrderChange?: (sortOrder: 'asc' | 'desc') => void;
  sortByOptions?: { value: string; label: string }[];
  showSorting?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  pagination,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20, 50],
  showPageSizeSelector = true,
  showTotalRecords = true,
  compact = false,
  style,
  sortBy,
  sortOrder,
  onSortByChange,
  onSortOrderChange,
  sortByOptions = [
    { value: 'createdAt', label: 'Created Date' },
    { value: 'updatedAt', label: 'Updated Date' },
    { value: 'title', label: 'Title' },
    { value: 'name', label: 'Name' },
  ],
  showSorting = false,
}) => {
  const theme = themeSelector();
  const { t } = useTranslation();

  if (
    theme === undefined ||
    !pagination ||
    typeof pagination.currentPage !== 'number' ||
    typeof pagination.totalPages !== 'number' ||
    !Array.isArray(pageSizeOptions) ||
    pageSizeOptions.length === 0 ||
    typeof pageSize !== 'number' ||
    pageSize <= 0 ||
    !pageSizeOptions.includes(pageSize) ||
    typeof onPageChange !== 'function' ||
    typeof onPageSizeChange !== 'function'
  ) {
    return null;
  }

  const handlePreviousPage = () => {
    if (pagination.hasPrevPage) onPageChange(pagination.currentPage - 1);
  };
  const handleNextPage = () => {
    if (pagination.hasNextPage) onPageChange(pagination.currentPage + 1);
  };
  const handlePageSizeChange = (newPageSize: number) => {
    onPageSizeChange(newPageSize);
    onPageChange(1);
  };

  const PAGINATION_HEIGHT = 50;
  const BTN_SIZE = 28;
  const btnStyle = (enabled: boolean) => ({
    opacity: enabled ? 1 : 0.5,
    width: BTN_SIZE,
    height: BTN_SIZE,
    borderRadius: BTN_SIZE / 2,
    backgroundColor: theme ? '#ffffff' : '#374151',
    borderWidth: 1,
    borderColor: theme ? '#d1d5db' : '#4b5563',
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
  });
  const containerProps = {
    height: PAGINATION_HEIGHT,
    paddingHorizontal: '$3',
    borderRadius: 10,
    backgroundColor: theme ? '#f8fafc' : '#1f2937',
    borderTopWidth: 1,
    borderTopColor: theme ? '#e2e8f0' : '#374151',
    alignItems: 'center' as const,
    overflow: 'visible' as const,
    zIndex: 20,
    style,
  };

  if (compact) {
    return (
      <XStack justifyContent="space-between" {...containerProps}>
        <SizableText size="$2" color={theme ? '#64748b' : '#9ca3af'}>
          Page {pagination.currentPage} of {pagination.totalPages}
        </SizableText>
        <XStack alignItems="center" gap="$1.5">
          <Pressable
            onPress={handlePreviousPage}
            disabled={!pagination.hasPrevPage}
            style={btnStyle(pagination.hasPrevPage)}
          >
            <AntDesign name="left" size={14} color={theme ? '#374151' : '#f3f4f6'} />
          </Pressable>
          <Pressable
            onPress={handleNextPage}
            disabled={!pagination.hasNextPage}
            style={btnStyle(pagination.hasNextPage)}
          >
            <AntDesign name="right" size={14} color={theme ? '#374151' : '#f3f4f6'} />
          </Pressable>
        </XStack>
      </XStack>
    );
  }

  return (
    <XStack {...containerProps}>
      <XStack alignSelf="stretch" flex={1} justifyContent="space-between" alignItems="center" gap="$3">
        <XStack alignItems="center" gap="$2" flexShrink={1}>
          <SizableText size="$2" color={theme ? '#64748b' : '#9ca3af'}>
            Page {pagination.currentPage} of {pagination.totalPages}
          </SizableText>
          {showTotalRecords && (
            <SizableText size="$2" color={theme ? '#64748b' : '#9ca3af'}>
              ({pagination.totalRecords} total)
            </SizableText>
          )}
        </XStack>
        {showSorting && onSortByChange && onSortOrderChange && (
          <XStack alignItems="center" gap="$2" flexShrink={0}>
            <SizableText size="$2" color={theme ? '#64748b' : '#9ca3af'}>
              {t('Sort by:')}
            </SizableText>
            <AppSelect
              size="compact"
              width={130}
              minWidth={130}
              openDirection="up"
              value={sortBy || 'updatedAt'}
              onValueChange={onSortByChange}
              placeholder={t('Select field')}
              options={sortByOptions.map((option) => ({
                label: t(option.label),
                value: option.value,
              }))}
            />
            <AppSelect
              size="compact"
              width={90}
              minWidth={90}
              openDirection="up"
              value={sortOrder || 'desc'}
              onValueChange={(value) => onSortOrderChange(value as 'asc' | 'desc')}
              placeholder={t('Order')}
              options={[
                { label: t('Ascending'), value: 'asc' },
                { label: t('Descending'), value: 'desc' },
              ]}
            />
          </XStack>
        )}
        <XStack alignItems="center" gap="$1.5" flexShrink={0}>
          <Pressable
            onPress={handlePreviousPage}
            disabled={!pagination.hasPrevPage}
            style={btnStyle(pagination.hasPrevPage)}
          >
            <AntDesign name="left" size={14} color={theme ? '#374151' : '#f3f4f6'} />
          </Pressable>
          {showPageSizeSelector && (
            <XStack alignItems="center" gap="$1.5">
              <SizableText size="$2" color={theme ? '#64748b' : '#9ca3af'}>
                {t('Show:')}
              </SizableText>
              <AppSelect
                size="compact"
                width={56}
                minWidth={56}
                openDirection="up"
                value={pageSize.toString()}
                onValueChange={(value) => handlePageSizeChange(Number(value))}
                placeholder={t('Select')}
                options={pageSizeOptions.map((size) => ({
                  label: String(size),
                  value: String(size),
                }))}
              />
            </XStack>
          )}
          <Pressable
            onPress={handleNextPage}
            disabled={!pagination.hasNextPage}
            style={btnStyle(pagination.hasNextPage)}
          >
            <AntDesign name="right" size={14} color={theme ? '#374151' : '#f3f4f6'} />
          </Pressable>
        </XStack>
      </XStack>
    </XStack>
  );
};

export const CompactPagination: React.FC<Omit<PaginationProps, 'compact'>> = (props) => (
  <Pagination {...props} compact={true} />
);
export const FullPagination: React.FC<Omit<PaginationProps, 'compact'>> = (props) => (
  <Pagination {...props} compact={false} />
);
export default Pagination;
